// Scroll Manager Utility for Fortisys Website
// Handles smooth scrolling and scroll-related functionality

export class ScrollManager {
  constructor(options = {}) {
    this.options = {
      smoothScrollDuration: 800,
      easing: 'easeInOutCubic',
      offset: 80,
      animationThreshold: 0.1,
      animationRootMargin: '0px 0px -100px 0px',
      ...options
    };

    this.isScrolling = false;
    this.animatedElements = new Set();
    this.animationObserver = null;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupSmoothScrolling();
    this.setupScrollAnimations();
  }

  bindEvents() {
    // Handle anchor link clicks
    this.handleAnchorClick = this.handleAnchorClick.bind(this);
    document.addEventListener('click', this.handleAnchorClick);

    // Handle scroll to top functionality
    this.setupScrollToTop();

    // Handle scroll-based header changes
    this.setupScrollHeader();
  }

  setupSmoothScrolling() {
    // Enable CSS smooth scrolling as fallback
    if ('scrollBehavior' in document.documentElement.style) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }
  }

  setupScrollAnimations() {
    // Setup intersection observer for scroll animations
    if ('IntersectionObserver' in window) {
      this.animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            this.animatedElements.add(entry.target);
            this.animationObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: this.options.animationThreshold,
        rootMargin: this.options.animationRootMargin
      });

      // Find and observe elements that should animate
      this.observeAnimatableElements();
    }
  }

  observeAnimatableElements() {
    const animatableSelectors = [
      '.value-props',
      '.platform-overview',
      '.industry-solutions',
      '.trust-indicators',
      '.executive-roi',
      '.card',
      '.platform-feature',
      '.roi-stat'
    ];

    animatableSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (this.animationObserver) {
          this.animationObserver.observe(element);
        }
      });
    });
  }

  setupScrollHeader() {
    let ticking = false;

    const handleScroll = () => {
      const scrollTop = this.getScrollTop();
      const header = document.querySelector('.header');

      if (header) {
        // Add scrolled class when user scrolls down
        if (scrollTop > 100) {
          header.classList.add('header--scrolled');
        } else {
          header.classList.remove('header--scrolled');
        }
      }

      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(handleScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  handleAnchorClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (href === '#') return;

    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      event.preventDefault();
      this.scrollToElement(targetElement);

      // Update URL hash without triggering scroll
      if (history.replaceState) {
        history.replaceState(null, null, href);
      }
    }
  }

  scrollToElement(element, customOffset = null) {
    if (this.isScrolling) return;

    const elementTop = element.offsetTop;
    const offset = customOffset !== null ? customOffset : this.options.offset;
    const targetPosition = Math.max(0, elementTop - offset);

    this.scrollToPosition(targetPosition);
  }

  scrollToPosition(position) {
    if (this.isScrolling) return;

    this.isScrolling = true;
    const startPosition = window.pageYOffset;
    const distance = position - startPosition;
    const duration = this.options.smoothScrollDuration;
    let startTime = null;

    const animateScroll = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeProgress = this.easing(progress);
      const currentPosition = startPosition + (distance * easeProgress);

      window.scrollTo(0, currentPosition);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        this.isScrolling = false;
        this.onScrollComplete();
      }
    };

    requestAnimationFrame(animateScroll);
  }

  setupScrollToTop() {
    // Create scroll to top button if it doesn't exist
    let scrollToTopBtn = document.querySelector('.scroll-to-top');

    if (!scrollToTopBtn) {
      scrollToTopBtn = document.createElement('button');
      scrollToTopBtn.className = 'scroll-to-top';
      scrollToTopBtn.innerHTML = '↑';
      scrollToTopBtn.setAttribute('aria-label', 'Scroll to top');
      scrollToTopBtn.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: 50px;
        height: 50px;
        background-color: var(--color-primary, #1a365d);
        color: var(--color-white, #ffffff);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
        z-index: 1000;
        font-size: 18px;
        font-weight: bold;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `;

      document.body.appendChild(scrollToTopBtn);
    }

    // Show/hide scroll to top button based on scroll position
    const toggleScrollToTop = () => {
      const scrollTop = this.getScrollTop();
      const shouldShow = scrollTop > 300;

      if (shouldShow) {
        scrollToTopBtn.style.opacity = '1';
        scrollToTopBtn.style.visibility = 'visible';
      } else {
        scrollToTopBtn.style.opacity = '0';
        scrollToTopBtn.style.visibility = 'hidden';
      }
    };

    // Handle scroll to top click
    scrollToTopBtn.addEventListener('click', () => {
      this.scrollToPosition(0);
    });

    // Handle scroll events
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(toggleScrollToTop, 100);
    }, { passive: true });

    // Initial check
    toggleScrollToTop();
  }

  // Easing functions
  easing(t) {
    switch (this.options.easing) {
    case 'easeInOutCubic':
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    case 'easeOutCubic':
      return 1 - Math.pow(1 - t, 3);
    case 'easeInCubic':
      return t * t * t;
    case 'linear':
    default:
      return t;
    }
  }

  onScrollComplete() {
    // Dispatch custom event when scroll animation completes
    const event = new CustomEvent('scrollComplete', {
      detail: { scrollManager: this }
    });

    window.dispatchEvent(event);
  }

  // Utility methods for getting scroll information
  getScrollTop() {
    return window.pageYOffset || document.documentElement.scrollTop;
  }

  getScrollHeight() {
    return Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
  }

  getWindowHeight() {
    return window.innerHeight || document.documentElement.clientHeight;
  }

  getScrollPercentage() {
    const scrollTop = this.getScrollTop();
    const scrollHeight = this.getScrollHeight();
    const windowHeight = this.getWindowHeight();

    return (scrollTop / (scrollHeight - windowHeight)) * 100;
  }

  isElementInViewport(element, threshold = 0) {
    const rect = element.getBoundingClientRect();
    const windowHeight = this.getWindowHeight();

    return (
      rect.top <= windowHeight * (1 - threshold) &&
      rect.bottom >= windowHeight * threshold
    );
  }

  // Public API methods
  scrollTo(target, offset = null) {
    if (typeof target === 'string') {
      const element = document.querySelector(target);
      if (element) {
        this.scrollToElement(element, offset);
      }
    } else if (typeof target === 'number') {
      this.scrollToPosition(target);
    } else if (target instanceof Element) {
      this.scrollToElement(target, offset);
    }
  }

  // Add new elements to animation observer
  observeElement(element) {
    if (this.animationObserver && element) {
      this.animationObserver.observe(element);
    }
  }

  destroy() {
    // Clean up event listeners
    document.removeEventListener('click', this.handleAnchorClick);

    // Disconnect animation observer
    if (this.animationObserver) {
      this.animationObserver.disconnect();
      this.animationObserver = null;
    }

    // Remove scroll to top button
    const scrollToTopBtn = document.querySelector('.scroll-to-top');
    if (scrollToTopBtn) {
      scrollToTopBtn.remove();
    }

    // Reset smooth scrolling
    document.documentElement.style.scrollBehavior = '';

    // Clear sets
    this.animatedElements.clear();
  }
}
